import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Wine, Calendar, MapPin, Mail, Phone, Building2, User, Briefcase, CheckCircle2, Clock, Shield, Users, Check, X, RefreshCw, LogOut, Download, QrCode } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_trade-rsvp-vinofest/artifacts/l6acytpo_VINOFEST%20INDIA%20%20IG%20TEMPLATE.png";

const SUPPORTER_LOGOS = {
  altvibes: "https://customer-assets.emergentagent.com/job_trade-rsvp-vinofest/artifacts/033pnvsd_Green%20-%20AltVibes%20LOGO_page-0001.jpg",
  advantageAustria: "https://customer-assets.emergentagent.com/job_trade-rsvp-vinofest/artifacts/3uun20kq_ADVANTAGE_AUSTRIA_4C_NEGATIVE_TYPO_0.9cmUG.png",
  aperluxGroup: "https://customer-assets.emergentagent.com/job_trade-rsvp-vinofest/artifacts/h669hjt6_APERLUX%20GROUP%20LOGO%20%285%29.png"
};

const GUEST_CATEGORIES = [
  { value: "importer", label: "Importer" },
  { value: "distributor", label: "Distributor" },
  { value: "horeca", label: "HORECA" },
  { value: "hni_guest", label: "HNI Guest" },
];

const MASTERCLASSES = [
  {
    id: "masterclass_1",
    time: "4:30 PM",
    title: "Exploring the vibrant world of white wine in northern Austria",
    description: "The country's exceptional terroir in every bottle.",
    speakers: "With Alwin Jurtschitsch, Matthias Hager, Markus from Landgut Schöne Erde and Michael Gindl from Gut Gindl"
  },
  {
    id: "masterclass_2",
    time: "5:45 PM",
    title: "Grüner Veltliner uncovered - One Grape, many expressions",
    description: "",
    speakers: "With Martin Pasler and Christian Nastl"
  },
  {
    id: "masterclass_3",
    time: "7:00 PM",
    title: "Blaufränkisch: one incredible grape with endless potential!",
    description: "A spicy deep dive with amazing Austrian icons.",
    speakers: "With Franz Weninger, Lydia Nittnaus, Ulrich from Judith Beck, Maria & Alex Koppitsch, Julia from winery Tschida. Winery Karl Schnabel is represented by Kantharos - wine agency"
  }
];

// RSVP Landing Page Component
const RSVPLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    designation: "",
    mobile: "",
    email: "",
    masterclass_1: false,
    masterclass_2: false,
    masterclass_3: false,
    guest_category: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.company_name.trim()) {
      newErrors.company_name = "Company name is required";
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[\d\s\-+()]{10,20}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid mobile number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.guest_category) {
      newErrors.guest_category = "Please select your category";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/rsvp`, formData);
      setSuccessData(response.data);
      setIsSuccess(true);
      toast.success("Registration submitted successfully!");
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      company_name: "",
      designation: "",
      mobile: "",
      email: "",
      masterclass_1: false,
      masterclass_2: false,
      masterclass_3: false,
      guest_category: "",
    });
    setErrors({});
    setIsSuccess(false);
    setSuccessData(null);
  };

  return (
    <div className="vino-container min-h-screen" data-testid="vino-container">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        {/* Hero Section - Wine Glass Image */}
        <div className="vino-hero col-span-1 lg:col-span-5 h-[45vh] sm:h-[50vh] lg:h-screen relative flex items-center justify-center" data-testid="hero-section">
          <img
            src={HERO_IMAGE}
            alt="VINOFEST INDIA - Taste of Austria"
            className="w-full h-full object-cover lg:object-cover object-center"
            style={{ objectPosition: 'center 30%' }}
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#5D1A21] lg:hidden"></div>
          <div className="hidden lg:block absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#5D1A21] to-transparent"></div>
        </div>

        {/* Form Section */}
        <div className="vino-form-section col-span-1 lg:col-span-7 px-4 sm:px-6 py-8 sm:py-12 lg:px-12 xl:px-20 overflow-y-auto relative z-10" data-testid="form-section">
          {!isSuccess ? (
            <div className="max-w-xl mx-auto lg:mx-0">
              {/* Header */}
              <div className="mb-8 sm:mb-10 opacity-0 animate-fadeInUp">
                <p className="text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4 font-medium" data-testid="event-label">
                  Exclusive Trade Event
                </p>
                <h1 className="vino-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4" data-testid="main-heading">
                  <span className="gold-gradient-text">VINOFEST</span>{" "}
                  <span className="text-[#F5DEB3]">INDIA</span>
                </h1>
                <h2 className="vino-subheading text-lg sm:text-xl md:text-2xl italic mb-4 sm:mb-6" data-testid="sub-heading">
                  Taste of Austria
                </h2>
                <div className="vino-divider my-4 sm:my-6"></div>
                <p className="text-[#F5DEB3]/80 text-sm sm:text-base leading-relaxed" data-testid="event-description">
                  VINOFEST INDIA – Taste of Austria brings together <span className="text-[#D4AF37] font-semibold">30+ wineries from Austria</span>, offering a dedicated platform for professional engagement through tastings, masterclasses, and structured networking.
                </p>
              </div>

              {/* Event Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-10 opacity-0 animate-fadeInUp stagger-1">
                <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-[#F5DEB3]">March 28th, 2026</p>
                    <p className="text-[10px] sm:text-xs text-[#F5DEB3]/50">3 PM - 8 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-[#F5DEB3]">Embassy of Austria</p>
                    <p className="text-[10px] sm:text-xs text-[#F5DEB3]/50">New Delhi</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80 sm:col-span-2">
                  <Wine className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0" />
                  <p className="text-xs sm:text-sm">Showcase of <span className="text-[#D4AF37] font-semibold">30+ Austrian Wineries</span></p>
                </div>
              </div>

              {/* RSVP Form */}
              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8" data-testid="rsvp-form">
                {/* Name */}
                <div className="opacity-0 animate-fadeInUp stagger-2">
                  <label className="vino-label" htmlFor="name">
                    <User className="w-3 h-3 inline mr-2" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="vino-input"
                    placeholder="Enter your full name"
                    data-testid="name-input"
                  />
                  {errors.name && <p className="vino-error" data-testid="name-error">{errors.name}</p>}
                </div>

                {/* Company Name */}
                <div className="opacity-0 animate-fadeInUp stagger-3">
                  <label className="vino-label" htmlFor="company_name">
                    <Building2 className="w-3 h-3 inline mr-2" />
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="vino-input"
                    placeholder="Enter your company name"
                    data-testid="company-input"
                  />
                  {errors.company_name && (
                    <p className="vino-error" data-testid="company-error">{errors.company_name}</p>
                  )}
                  <p className="text-[#F5DEB3]/40 text-[10px] sm:text-xs mt-2">
                    This event is exclusively for Trade Representatives
                  </p>
                </div>

                {/* Designation */}
                <div className="opacity-0 animate-fadeInUp stagger-4">
                  <label className="vino-label" htmlFor="designation">
                    <Briefcase className="w-3 h-3 inline mr-2" />
                    Designation
                  </label>
                  <input
                    type="text"
                    id="designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    className="vino-input"
                    placeholder="Your title or position"
                    data-testid="designation-input"
                  />
                </div>

                {/* Guest Category */}
                <div className="opacity-0 animate-fadeInUp stagger-5">
                  <label className="vino-label" htmlFor="guest_category">
                    <Wine className="w-3 h-3 inline mr-2" />
                    Guest Category *
                  </label>
                  <select
                    id="guest_category"
                    name="guest_category"
                    value={formData.guest_category}
                    onChange={handleChange}
                    className="vino-select"
                    data-testid="category-select"
                  >
                    <option value="">Select your category</option>
                    {GUEST_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                  {errors.guest_category && (
                    <p className="vino-error" data-testid="category-error">{errors.guest_category}</p>
                  )}
                </div>

                {/* Mobile */}
                <div className="opacity-0 animate-fadeInUp stagger-5">
                  <label className="vino-label" htmlFor="mobile">
                    <Phone className="w-3 h-3 inline mr-2" />
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="vino-input"
                    placeholder="+91 98765 43210"
                    data-testid="mobile-input"
                  />
                  {errors.mobile && <p className="vino-error" data-testid="mobile-error">{errors.mobile}</p>}
                </div>

                {/* Email */}
                <div className="opacity-0 animate-fadeInUp stagger-6">
                  <label className="vino-label" htmlFor="email">
                    <Mail className="w-3 h-3 inline mr-2" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="vino-input"
                    placeholder="your@email.com"
                    data-testid="email-input"
                  />
                  {errors.email && <p className="vino-error" data-testid="email-error">{errors.email}</p>}
                </div>

                {/* Masterclass Participation */}
                <div className="opacity-0 animate-fadeInUp stagger-7">
                  <label className="vino-label mb-4 block">
                    <Wine className="w-3 h-3 inline mr-2" />
                    Masterclass Participation (Select one or more)
                  </label>
                  <div className="space-y-4">
                    {MASTERCLASSES.map((mc) => (
                      <div 
                        key={mc.id}
                        className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 border border-[#D4AF37]/20 bg-[#2D0A10]/40 hover:bg-[#2D0A10]/60 transition-colors"
                      >
                        <input
                          type="checkbox"
                          id={mc.id}
                          name={mc.id}
                          checked={formData[mc.id]}
                          onChange={handleChange}
                          className="vino-checkbox mt-1"
                          data-testid={`${mc.id}-checkbox`}
                        />
                        <label
                          htmlFor={mc.id}
                          className="text-[#F5DEB3]/90 text-xs sm:text-sm cursor-pointer leading-relaxed flex-1"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[#D4AF37] font-semibold">Masterclass {mc.id.split('_')[1]}</span>
                            <span className="text-[#D4AF37]/70 text-xs">{mc.time}</span>
                          </div>
                          <p className="text-[#F5DEB3] font-medium text-sm mb-1">{mc.title}</p>
                          {mc.description && (
                            <p className="text-[#F5DEB3]/60 text-xs mb-1">{mc.description}</p>
                          )}
                          <p className="text-[#F5DEB3]/50 text-xs italic">{mc.speakers}</p>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2 sm:pt-4 opacity-0 animate-fadeInUp stagger-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="vino-btn-primary w-full sm:w-auto min-w-[180px] sm:min-w-[200px] flex items-center justify-center gap-2 text-sm sm:text-base"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Wine className="w-4 h-4" />
                        Register Now
                      </>
                    )}
                  </button>
                </div>

                {/* Note */}
                <p className="text-[#F5DEB3]/40 text-[10px] sm:text-xs mt-4 sm:mt-6 opacity-0 animate-fadeInUp stagger-8">
                  By registering, you agree to receive communications regarding VINOFEST INDIA.
                  Your invitation must be presented at the venue for entry.
                </p>
              </form>

              {/* Supporters Section */}
              <div className="mt-12 sm:mt-16 pt-8 sm:pt-10 border-t border-[#D4AF37]/20">
                <p className="text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-6 sm:mb-8 text-center">
                  Supported By
                </p>
                <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                  <img 
                    src={SUPPORTER_LOGOS.advantageAustria} 
                    alt="Advantage Austria" 
                    className="h-12 sm:h-16 object-contain"
                    data-testid="logo-advantage-austria"
                  />
                  <img 
                    src={SUPPORTER_LOGOS.altvibes} 
                    alt="AltVibes" 
                    className="h-12 sm:h-16 object-contain bg-white rounded p-1"
                    data-testid="logo-altvibes"
                  />
                </div>
              </div>

              {/* Powered By Section */}
              <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-[#D4AF37]/20">
                <p className="text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-center">
                  Powered By
                </p>
                <div className="flex items-center justify-center">
                  <img 
                    src={SUPPORTER_LOGOS.aperluxGroup} 
                    alt="Aperlux Group" 
                    className="h-24 sm:h-32 object-contain"
                    data-testid="logo-aperlux-group"
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Success State - Digital Invite */
            <div className="max-w-xl mx-auto lg:mx-0">
              <div className="vino-success-card animate-fadeInUp relative" data-testid="success-card">
                {/* Corner Decorations */}
                <div className="vino-corner-decoration vino-corner-tl"></div>
                <div className="vino-corner-decoration vino-corner-tr"></div>
                <div className="vino-corner-decoration vino-corner-bl"></div>
                <div className="vino-corner-decoration vino-corner-br"></div>

                {/* Success Icon */}
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-[#D4AF37] flex items-center justify-center animate-pulse-gold">
                    <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-[#D4AF37]" />
                  </div>
                </div>

                {/* Heading */}
                <div className="text-center mb-6 sm:mb-8">
                  <p className="text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-2">
                    Application Received
                  </p>
                  <h2 className="vino-heading text-xl sm:text-2xl md:text-3xl font-bold gold-gradient-text mb-3 sm:mb-4" data-testid="success-heading">
                    Thank You, {successData?.name}!
                  </h2>
                  <div className="vino-divider my-3 sm:my-4"></div>
                </div>

                {/* Registration Details */}
                <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 text-sm">
                  <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80">
                    <Building2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span>{successData?.company_name}</span>
                  </div>
                  {successData?.designation && (
                    <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80">
                      <Briefcase className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                      <span>{successData?.designation}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80">
                    <Mail className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="break-all">{successData?.email}</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80">
                    <Calendar className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span>March 28th, 2026 | 3 PM - 8 PM</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 text-[#F5DEB3]/80">
                    <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span>Embassy of Austria, New Delhi</span>
                  </div>
                  {(successData?.masterclass_1 || successData?.masterclass_2 || successData?.masterclass_3) && (
                    <div className="mt-3 p-3 border border-[#D4AF37]/20 bg-[#2D0A10]/40">
                      <p className="text-[#D4AF37] text-xs font-semibold mb-2">Masterclass Selections:</p>
                      {successData?.masterclass_1 && (
                        <p className="text-[#F5DEB3]/80 text-xs">✓ MC1 (4:30 PM) - White wines of northern Austria</p>
                      )}
                      {successData?.masterclass_2 && (
                        <p className="text-[#F5DEB3]/80 text-xs">✓ MC2 (5:45 PM) - Grüner Veltliner uncovered</p>
                      )}
                      {successData?.masterclass_3 && (
                        <p className="text-[#F5DEB3]/80 text-xs">✓ MC3 (7:00 PM) - Blaufränkisch deep dive</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="vino-divider my-4 sm:my-6"></div>

                {/* Important Notice */}
                <div className="bg-[#1A0507]/50 p-3 sm:p-4 border-l-2 border-[#D4AF37] mb-4 sm:mb-6">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#F5DEB3] text-xs sm:text-sm font-medium mb-1">
                        What happens next?
                      </p>
                      <p className="text-[#F5DEB3]/60 text-xs sm:text-sm leading-relaxed">
                        Your application will be reviewed within <strong className="text-[#D4AF37]">48 hours</strong>. 
                        Approved attendees will receive a formal Digital Invite with QR code via email.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Entry Notice */}
                <div className="text-center p-3 sm:p-4 border border-[#D4AF37]/30 bg-[#2D0A10]/60">
                  <p className="text-[#F5DEB3]/80 text-xs sm:text-sm">
                    <strong className="text-[#D4AF37]">Note:</strong> Each invite is valid for{" "}
                    <strong className="text-[#D4AF37]">one person</strong> only and must be presented at the venue for entry to VINOFEST INDIA - Taste of Austria.
                  </p>
                </div>

                {/* New Registration Button */}
                <div className="mt-6 sm:mt-8 text-center">
                  <button
                    onClick={resetForm}
                    className="vino-btn-secondary text-sm sm:text-base"
                    data-testid="new-registration-button"
                  >
                    Submit Another Registration
                  </button>
                </div>
              </div>

              {/* Supporters in Success */}
              <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-[#D4AF37]/20">
                <p className="text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-[0.2em] text-center mb-4 sm:mb-6">
                  Supported By
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
                  <img 
                    src={SUPPORTER_LOGOS.advantageAustria} 
                    alt="Advantage Austria" 
                    className="h-10 sm:h-14 object-contain"
                  />
                  <img 
                    src={SUPPORTER_LOGOS.altvibes} 
                    alt="AltVibes" 
                    className="h-10 sm:h-14 object-contain bg-white rounded p-1"
                  />
                </div>
              </div>

              {/* Powered By in Success */}
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-[#D4AF37]/20">
                <p className="text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-[0.2em] text-center mb-3 sm:mb-4">
                  Powered By
                </p>
                <div className="flex items-center justify-center">
                  <img 
                    src={SUPPORTER_LOGOS.aperluxGroup} 
                    alt="Aperlux Group" 
                    className="h-20 sm:h-28 object-contain"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 sm:mt-16 text-center lg:text-left">
            <p className="text-[#F5DEB3]/30 text-[10px] sm:text-xs uppercase tracking-widest">
              VINOFEST INDIA © 2026
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};


// Admin Dashboard Component
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedRsvp, setSelectedRsvp] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      toast.success("Login successful");
      fetchData();
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [rsvpsRes, statsRes] = await Promise.all([
        axios.get(`${API}/rsvp${filterStatus ? `?status=${filterStatus}` : ''}`),
        axios.get(`${API}/admin/stats`)
      ]);
      setRegistrations(rsvpsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn, filterStatus]);

  const updateStatus = async (id, status) => {
    try {
      const response = await axios.patch(`${API}/rsvp/${id}/status`, { status });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resendInvite = async (id) => {
    try {
      const response = await axios.post(`${API}/rsvp/${id}/resend-invite`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to resend invite");
    }
  };

  const viewQrCode = async (rsvp) => {
    try {
      const response = await axios.get(`${API}/rsvp/${rsvp.id}/qrcode`);
      setQrCodeData(response.data);
      setSelectedRsvp(rsvp);
      setShowQrModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get QR code");
    }
  };

  const downloadQrCode = () => {
    if (!qrCodeData?.qr_code) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrCodeData.qr_code}`;
    link.download = `vinofest-qr-${qrCodeData.name.replace(/\s+/g, '-')}.png`;
    link.click();
  };

  const getCategoryLabel = (value) => {
    const cat = GUEST_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#5D1A21] flex items-center justify-center p-4">
        <div className="vino-card max-w-md w-full">
          <div className="text-center mb-8">
            <Shield className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
            <h1 className="vino-heading text-2xl font-bold gold-gradient-text">Admin Access</h1>
            <p className="text-[#F5DEB3]/60 text-sm mt-2">VINOFEST INDIA Dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="vino-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="vino-input"
                placeholder="Enter admin password"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="vino-btn-primary w-full flex items-center justify-center gap-2"
              data-testid="admin-login-button"
            >
              {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
              Access Dashboard
            </button>
          </form>
          <button 
            onClick={() => navigate('/')}
            className="w-full mt-4 text-[#D4AF37] text-sm hover:underline"
          >
            Back to Registration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#5D1A21] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="vino-heading text-xl sm:text-2xl lg:text-3xl font-bold gold-gradient-text">Admin Dashboard</h1>
          <p className="text-[#F5DEB3]/60 text-xs sm:text-sm mt-1">VINOFEST INDIA - Registration Management</p>
        </div>
        <button
          onClick={() => setIsLoggedIn(false)}
          className="vino-btn-secondary flex items-center gap-2 text-sm"
          data-testid="admin-logout-button"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="vino-card text-center p-4">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-2xl sm:text-3xl font-bold text-[#F5DEB3]">{stats.total}</p>
            <p className="text-[10px] sm:text-xs text-[#F5DEB3]/60 uppercase tracking-wider">Total</p>
          </div>
          <div className="vino-card text-center p-4">
            <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl sm:text-3xl font-bold text-yellow-500">{stats.pending}</p>
            <p className="text-[10px] sm:text-xs text-[#F5DEB3]/60 uppercase tracking-wider">Pending</p>
          </div>
          <div className="vino-card text-center p-4">
            <Check className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl sm:text-3xl font-bold text-green-500">{stats.approved}</p>
            <p className="text-[10px] sm:text-xs text-[#F5DEB3]/60 uppercase tracking-wider">Approved</p>
          </div>
          <div className="vino-card text-center p-4">
            <X className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
            <p className="text-2xl sm:text-3xl font-bold text-red-500">{stats.rejected}</p>
            <p className="text-[10px] sm:text-xs text-[#F5DEB3]/60 uppercase tracking-wider">Rejected</p>
          </div>
        </div>
      )}

      {/* Filter & Refresh */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[#F5DEB3]/60 text-xs sm:text-sm">Filter:</span>
          {["", "pending", "approved", "rejected"].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1 text-xs sm:text-sm rounded ${
                filterStatus === status
                  ? "bg-[#D4AF37] text-[#1A0507]"
                  : "border border-[#D4AF37]/30 text-[#F5DEB3]/70 hover:bg-[#D4AF37]/10"
              }`}
              data-testid={`filter-${status || 'all'}`}
            >
              {status ? status.charAt(0).toUpperCase() + status.slice(1) : "All"}
            </button>
          ))}
        </div>
        <button
          onClick={fetchData}
          className="vino-btn-secondary flex items-center gap-2 text-sm"
          data-testid="refresh-button"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Registrations Table */}
      <div className="vino-card overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-[#D4AF37]/30">
              <th className="text-left p-3 text-[#D4AF37] text-xs uppercase tracking-wider">Name</th>
              <th className="text-left p-3 text-[#D4AF37] text-xs uppercase tracking-wider">Company</th>
              <th className="text-left p-3 text-[#D4AF37] text-xs uppercase tracking-wider">Category</th>
              <th className="text-left p-3 text-[#D4AF37] text-xs uppercase tracking-wider">Contact</th>
              <th className="text-left p-3 text-[#D4AF37] text-xs uppercase tracking-wider">Masterclass</th>
              <th className="text-left p-3 text-[#D4AF37] text-xs uppercase tracking-wider">Status</th>
              <th className="text-left p-3 text-[#D4AF37] text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations.map((rsvp) => (
              <tr key={rsvp.id} className="border-b border-[#D4AF37]/10 hover:bg-[#D4AF37]/5" data-testid={`rsvp-row-${rsvp.id}`}>
                <td className="p-3">
                  <p className="text-[#F5DEB3] font-medium text-sm">{rsvp.name}</p>
                  <p className="text-[#F5DEB3]/50 text-xs">{rsvp.designation || '-'}</p>
                </td>
                <td className="p-3 text-[#F5DEB3]/80 text-sm">{rsvp.company_name}</td>
                <td className="p-3 text-[#F5DEB3]/80 text-sm">{getCategoryLabel(rsvp.guest_category)}</td>
                <td className="p-3">
                  <p className="text-[#F5DEB3]/80 text-sm">{rsvp.email}</p>
                  <p className="text-[#F5DEB3]/50 text-xs">{rsvp.mobile}</p>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {rsvp.masterclass_1 && <span className="text-green-500 text-xs">MC1</span>}
                    {rsvp.masterclass_2 && <span className="text-green-500 text-xs">MC2</span>}
                    {rsvp.masterclass_3 && <span className="text-green-500 text-xs">MC3</span>}
                    {!rsvp.masterclass_1 && !rsvp.masterclass_2 && !rsvp.masterclass_3 && (
                      <span className="text-[#F5DEB3]/40 text-xs">None</span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    rsvp.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                    rsvp.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {rsvp.status.charAt(0).toUpperCase() + rsvp.status.slice(1)}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    {rsvp.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateStatus(rsvp.id, 'approved')}
                          className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"
                          title="Approve"
                          data-testid={`approve-${rsvp.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => updateStatus(rsvp.id, 'rejected')}
                          className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                          title="Reject"
                          data-testid={`reject-${rsvp.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {rsvp.status === 'approved' && (
                      <>
                        <button
                          onClick={() => viewQrCode(rsvp)}
                          className="p-1.5 bg-[#D4AF37]/20 text-[#D4AF37] rounded hover:bg-[#D4AF37]/30"
                          title="View QR Code"
                          data-testid={`qr-${rsvp.id}`}
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => resendInvite(rsvp.id)}
                          className="p-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30"
                          title="Resend Invite"
                          data-testid={`resend-${rsvp.id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {registrations.length === 0 && (
              <tr>
                <td colSpan="7" className="p-8 text-center text-[#F5DEB3]/50">
                  No registrations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* QR Code Modal */}
      {showQrModal && qrCodeData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowQrModal(false)}>
          <div className="vino-card max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <div className="text-center">
              <h3 className="vino-heading text-xl font-bold gold-gradient-text mb-2">Entry QR Code</h3>
              <p className="text-[#F5DEB3]/70 text-sm mb-4">{selectedRsvp?.name}</p>
              <div className="bg-white p-4 rounded inline-block mb-4">
                <img 
                  src={`data:image/png;base64,${qrCodeData.qr_code}`} 
                  alt="QR Code" 
                  className="w-48 h-48"
                />
              </div>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={downloadQrCode}
                  className="vino-btn-primary flex items-center gap-2 text-sm"
                  data-testid="download-qr"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={() => setShowQrModal(false)}
                  className="vino-btn-secondary text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


function App() {
  return (
    <div className="App">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#2D0A10',
            color: '#F5DEB3',
            border: '1px solid #D4AF37',
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RSVPLandingPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
